// Simple global search registry for adding per-page search terms at runtime
// Usage (in any page/component):
// import { registerSearchTerms, unregisterSearchTerms } from '../lib/searchRegistry';
// useEffect(() => {
//   const id = registerSearchTerms({ url: '/contact', terms: { mr: ['कार्यालये', 'कार्यकाळ'], en: ['offices', 'hours'] } });
//   return () => unregisterSearchTerms(id);
// }, []);

const globalKey = '__MLWB_SEARCH_REGISTRY__';

function getStore() {
  if (!window[globalKey]) {
    window[globalKey] = { seq: 0, entries: new Map() };
  }
  return window[globalKey];
}

export function registerSearchTerms(entry) {
  const store = getStore();
  const url = typeof entry?.url === 'string' ? entry.url : '/';
  
  // Check if an entry for this URL already exists
  let existingEntry = null;
  let existingId = null;
  for (const [id, e] of store.entries.entries()) {
    if (e.url === url) {
      existingEntry = e;
      existingId = id;
      break;
    }
  }
  
  // If exists, update it; otherwise create new
  if (existingEntry) {
    const updated = {
      ...existingEntry,
      terms: {
        mr: Array.isArray(entry?.terms?.mr) ? [...(existingEntry.terms.mr || []), ...entry.terms.mr] : existingEntry.terms.mr,
        en: Array.isArray(entry?.terms?.en) ? [...(existingEntry.terms.en || []), ...entry.terms.en] : existingEntry.terms.en
      }
    };
    store.entries.set(existingId, updated);
    return existingId;
  } else {
    const id = `e_${++store.seq}`;
    const safe = {
      id,
      url,
      terms: {
        mr: Array.isArray(entry?.terms?.mr) ? entry.terms.mr : [],
        en: Array.isArray(entry?.terms?.en) ? entry.terms.en : []
      }
    };
    store.entries.set(id, safe);
    return id;
  }
}

export function unregisterSearchTerms(id) {
  const store = getStore();
  store.entries.delete(id);
}

export function getAllSearchEntries() {
  const store = getStore();
  return Array.from(store.entries.values());
}

// Initialize all pages on app load for first-time visitors
// This ensures all pages are searchable immediately without needing to visit them first
export function initializePageIndex(pageIndex) {
  if (!pageIndex || !Array.isArray(pageIndex)) {
    return;
  }

  pageIndex.forEach(page => {
    if (page.url && page.terms) {
      registerSearchTerms({
        url: page.url,
        terms: {
          mr: Array.isArray(page.terms.mr) ? page.terms.mr : [],
          en: Array.isArray(page.terms.en) ? page.terms.en : []
        }
      });
    }
  });
}

// Auto-index current page content based on DOM text inside [data-read-section] elements
export function autoRegisterCurrentPage(url, { isMarathi, extraTerms = { mr: [], en: [] } } = {}) {
  // Fallback for non-browser environments (SSR/tests)
  if (typeof document === 'undefined') {
    return registerSearchTerms({
      url,
      terms: {
        mr: extraTerms.mr || [],
        en: extraTerms.en || []
      }
    });
  }

  // Extract text from all data-read-section elements
  // This includes paragraphs, headings, lists, and all visible text content
  const nodes = Array.from(document.querySelectorAll('[data-read-section]'));

  const fullText = nodes
    .map((n) => {
      // Get all text content, including nested elements
      // innerText is better than textContent as it respects CSS visibility
      const text = (n.innerText || n.textContent || '').replace(/\s+/g, ' ').trim();
      return text;
    })
    .filter(Boolean)
    .join(' ');

  const isMarathiFn = typeof isMarathi === 'function' ? isMarathi : () => false;

  // Combine extra terms with extracted content
  // Content is indexed in the current language
  const terms = {
    mr: [
      ...(extraTerms.mr || []),
      ...(isMarathiFn() && fullText ? [fullText] : [])
    ].filter(Boolean),
    en: [
      ...(extraTerms.en || []),
      ...(!isMarathiFn() && fullText ? [fullText] : [])
    ].filter(Boolean)
  };

  return registerSearchTerms({ url, terms });
}

